import type {
  IsolationNodeSummary,
  PageIsolationSelection,
} from 'features/knowledgeDocumentViewer/graph/IsolationControllerContext'

import type { LotoAttachmentGroup } from './lotoAttachmentGroups'
import type { LotoPageTarget } from './lotoPageLookups'
import { mergeAttachments } from './lotoReportDraft'
import {
  type LotoAttachment,
  type LotoIsolationPoint,
  lotoIsolationPointSchema,
  type LotoReport,
} from './lotoReportSchemas'

export type LotoPointKey = { pageId: string; nodeId: string }

export type LotoPointChanges = Partial<
  Pick<LotoIsolationPoint, 'isolation_position'>
>

export type LotoOtherPage = {
  systemDocumentId: string
  pageId: string
  documentName: string | undefined
  target: LotoPageTarget | undefined
  pointCount: number
}

function isPoint(point: LotoIsolationPoint, key: LotoPointKey): boolean {
  return point.page_id === key.pageId && point.node_id === key.nodeId
}

function newIsolationPoint(node: IsolationNodeSummary): LotoIsolationPoint {
  return lotoIsolationPointSchema.parse({
    node_id: node.nodeId,
    page_id: node.pageId,
    tag: node.label,
    description: node.subtitle ?? '',
  })
}

export function removeIsolationPoint(
  report: LotoReport,
  key: LotoPointKey
): LotoReport {
  return {
    ...report,
    isolation_points: report.isolation_points.filter(
      (point) => !isPoint(point, key)
    ),
  }
}

export function updateIsolationPoint(
  report: LotoReport,
  key: LotoPointKey,
  changes: LotoPointChanges
): LotoReport {
  return {
    ...report,
    isolation_points: report.isolation_points.map((point) =>
      isPoint(point, key) ? { ...point, ...changes } : point
    ),
  }
}

export function moveIsolationPoint(
  report: LotoReport,
  from: number,
  to: number
): LotoReport {
  const points = report.isolation_points
  const inRange = (index: number): boolean =>
    Number.isInteger(index) && index >= 0 && index < points.length
  if (from === to || !inRange(from) || !inRange(to)) return report
  const reordered = points.filter((_, index) => index !== from)
  reordered.splice(to, 0, points[from])
  return { ...report, isolation_points: reordered }
}

export function isolationPointId(point: LotoIsolationPoint): string {
  return `${point.page_id}/${point.node_id}`
}

export function isolationPointDropMove(
  ids: string[],
  activeId: string,
  overId: string | undefined
): { from: number; to: number } | undefined {
  if (overId === undefined || activeId === overId) return undefined
  const from = ids.indexOf(activeId)
  const to = ids.indexOf(overId)
  if (from === -1 || to === -1) return undefined
  return { from, to }
}

export function toggleIsolationPoint(
  report: LotoReport,
  node: IsolationNodeSummary,
  systemDocumentId: string
): LotoReport {
  if (report.isolation_points.some((point) => isPoint(point, node)))
    return removeIsolationPoint(report, node)
  return {
    ...report,
    attachments: mergeAttachments(report.attachments, [
      { system_document_id: systemDocumentId, page_ids: [node.pageId] },
    ]),
    isolation_points: [...report.isolation_points, newIsolationPoint(node)],
  }
}

export function isolationSelection(
  points: LotoIsolationPoint[]
): PageIsolationSelection[] {
  const pageIdToSelection = new Map<string, PageIsolationSelection>()
  points.forEach(({ page_id, node_id }, index) => {
    const entry = pageIdToSelection.get(page_id) ?? {
      pageId: page_id,
      nodeIds: [],
      badges: {},
    }
    pageIdToSelection.set(page_id, {
      ...entry,
      nodeIds: [...entry.nodeIds, node_id],
      badges: { ...entry.badges, [node_id]: String(index + 1) },
    })
  })
  return [...pageIdToSelection.values()]
}

export function pageIsolationPoints(
  points: LotoIsolationPoint[],
  pageId: string
): LotoIsolationPoint[] {
  return points.filter((point) => point.page_id === pageId)
}

function isCurrentPage(
  systemDocumentId: string,
  pageId: string,
  current: LotoAttachment
): boolean {
  return (
    systemDocumentId === current.system_document_id &&
    current.page_ids.includes(pageId)
  )
}

export function lotoOtherPages(
  groups: LotoAttachmentGroup[],
  points: LotoIsolationPoint[],
  current: LotoAttachment
): LotoOtherPage[] {
  return groups.flatMap(({ systemDocumentId, title, number, pages }) =>
    pages
      .filter(({ pageId }) => !isCurrentPage(systemDocumentId, pageId, current))
      .map(({ pageId, target }) => ({
        systemDocumentId,
        pageId,
        documentName: number ?? title,
        target,
        pointCount: pageIsolationPoints(points, pageId).length,
      }))
  )
}
